import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  FileText,
  User,
  Calendar,
  Shield,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Loader2,
  CheckCheck,
  Circle,
} from 'lucide-react';
import { onboardingAPI, OnboardingWorkflow, OnboardingStep, OnboardingActivityLog } from '../../lib/api';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { getApiErrorMessage } from '../../utils/errorUtils';

const statusColors: Record<string, string> = {
  invited: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  registered: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  documents_pending: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  questionnaires_pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  submitted: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  under_review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
  more_info_required: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  declined: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const stepTypeIcons: Record<string, React.ReactNode> = {
  company_info: <Building2 className="h-4 w-4" />,
  documents: <FileText className="h-4 w-4" />,
  questionnaire: <FileText className="h-4 w-4" />,
  code_of_conduct: <Shield className="h-4 w-4" />,
  review: <CheckCircle2 className="h-4 w-4" />,
};

export default function WorkflowDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workflow, setWorkflow] = useState<OnboardingWorkflow | null>(null);
  const [steps, setSteps] = useState<OnboardingStep[]>([]);
  const [timeline, setTimeline] = useState<OnboardingActivityLog[]>([]);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [infoRequest, setInfoRequest] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (id) {
      fetchWorkflow();
    }
  }, [id]);

  const fetchWorkflow = async () => {
    setLoading(true);
    try {
      const [workflowRes, timelineRes] = await Promise.all([
        onboardingAPI.getWorkflow(parseInt(id!)),
        onboardingAPI.getWorkflowTimeline(parseInt(id!)),
      ]);
      setWorkflow(workflowRes.data);
      setSteps(workflowRes.data.steps || []);
      setTimeline(timelineRes.data || []);
    } catch (error) {
      console.error('Failed to fetch workflow:', error);
      toast.error('Failed to load onboarding workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!workflow) return;
    setActionLoading(true);
    try {
      await onboardingAPI.approveWorkflow(workflow.id);
      toast.success('Supplier onboarding approved');
      setApproveDialogOpen(false);
      fetchWorkflow();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to approve'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!workflow || !declineReason.trim()) return;
    setActionLoading(true);
    try {
      await onboardingAPI.declineWorkflow(workflow.id, declineReason);
      toast.success('Supplier onboarding declined');
      setDeclineDialogOpen(false);
      setDeclineReason('');
      fetchWorkflow();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to decline'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestInfo = async () => {
    if (!workflow || !infoRequest.trim()) return;
    setActionLoading(true);
    try {
      await onboardingAPI.requestMoreInfo(workflow.id, infoRequest);
      toast.success('Information request sent');
      setInfoDialogOpen(false);
      setInfoRequest('');
      fetchWorkflow();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to send request'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="p-6 text-center">
        <h2 className="text-xl font-semibold">Workflow not found</h2>
        <Button variant="link" onClick={() => navigate('/onboarding')}>
          Back to Onboarding
        </Button>
      </div>
    );
  }

  const canApprove = workflow.status === 'submitted' || workflow.status === 'under_review';
  const canDecline = workflow.status !== 'approved' && workflow.status !== 'declined';
  const canRequestInfo = workflow.status !== 'approved' && workflow.status !== 'declined';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/onboarding')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{workflow.supplier_name}</h1>
              <Badge className={statusColors[workflow.status] || statusColors['invited']}>
                {workflow.status?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Onboarding workflow for {workflow.customer_name || 'your company'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canRequestInfo && (
            <Button variant="outline" onClick={() => setInfoDialogOpen(true)}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Request Info
            </Button>
          )}
          {canDecline && (
            <Button variant="outline" onClick={() => setDeclineDialogOpen(true)} className="text-destructive">
              <ThumbsDown className="h-4 w-4 mr-2" />
              Decline
            </Button>
          )}
          {canApprove && (
            <Button onClick={() => setApproveDialogOpen(true)}>
              <ThumbsUp className="h-4 w-4 mr-2" />
              Approve
            </Button>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Overall Progress</span>
            <span className="text-sm text-muted-foreground">
              {Math.round(workflow.progress_percentage || 0)}%
            </span>
          </div>
          <Progress value={workflow.progress_percentage || 0} className="h-2" />
          <div className="flex items-center justify-between mt-2 text-sm text-muted-foreground">
            <span>Step {workflow.current_step || 1} of {workflow.total_steps || 5}</span>
            {workflow.started_at && (
              <span>Started {formatDistanceToNow(new Date(workflow.started_at), { addSuffix: true })}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="steps">Steps</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Supplier Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Supplier Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <div className="flex items-center gap-3">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <div className="text-sm text-muted-foreground">Company Name</div>
                    <div className="font-medium">{workflow.supplier_name}</div>
                  </div>
                </div>
                {workflow.initial_risk_level && (
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Initial Risk Level</div>
                      <Badge variant={
                        workflow.initial_risk_level === 'high' ? 'destructive' :
                        workflow.initial_risk_level === 'medium' ? 'secondary' : 'default'
                      }>
                        {workflow.initial_risk_level}
                      </Badge>
                    </div>
                  </div>
                )}
                {workflow.calculated_risk_score !== null && workflow.calculated_risk_score !== undefined && (
                  <div className="flex items-center gap-3">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Calculated Risk Score</div>
                      <div className="font-medium">{workflow.calculated_risk_score} / 100</div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Key Dates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Key Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                {workflow.started_at && (
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Started</div>
                      <div className="font-medium">
                        {format(new Date(workflow.started_at), 'MMM d, yyyy h:mm a')}
                      </div>
                    </div>
                  </div>
                )}
                {workflow.submitted_at && (
                  <div className="flex items-center gap-3">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Submitted</div>
                      <div className="font-medium">
                        {format(new Date(workflow.submitted_at), 'MMM d, yyyy h:mm a')}
                      </div>
                    </div>
                  </div>
                )}
                {workflow.approved_at && (
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <div>
                      <div className="text-sm text-muted-foreground">Approved</div>
                      <div className="font-medium">
                        {format(new Date(workflow.approved_at), 'MMM d, yyyy h:mm a')}
                      </div>
                    </div>
                  </div>
                )}
                {workflow.declined_at && (
                  <div className="flex items-center gap-3">
                    <XCircle className="h-4 w-4 text-red-500" />
                    <div>
                      <div className="text-sm text-muted-foreground">Declined</div>
                      <div className="font-medium">
                        {format(new Date(workflow.declined_at), 'MMM d, yyyy h:mm a')}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Decline Reason */}
          {workflow.decline_reason && (
            <Card className="mt-6 border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950">
              <CardHeader>
                <CardTitle className="text-red-700 dark:text-red-400 flex items-center gap-2">
                  <XCircle className="h-5 w-5" />
                  Decline Reason
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-red-700 dark:text-red-400">{workflow.decline_reason}</p>
              </CardContent>
            </Card>
          )}

          {/* More Info Feedback */}
          {workflow.more_info_feedback && (
            <Card className="mt-6 border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950">
              <CardHeader>
                <CardTitle className="text-amber-700 dark:text-amber-400 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Information Requested
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-amber-700 dark:text-amber-400">{workflow.more_info_feedback}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Steps Tab */}
        <TabsContent value="steps" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Onboarding Steps</CardTitle>
              <CardDescription>
                Track progress through each step of the onboarding process
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {steps.length === 0 ? (
                  <p className="text-muted-foreground text-center py-4">
                    No steps configured for this workflow
                  </p>
                ) : (
                  steps.map((step, _index) => (
                    <div
                      key={step.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border ${
                        step.is_completed
                          ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-900'
                          : 'bg-muted/30'
                      }`}
                    >
                      <div className={`mt-0.5 p-2 rounded-full ${
                        step.is_completed
                          ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                          : 'bg-muted text-muted-foreground'
                      }`}>
                        {step.is_completed ? (
                          <CheckCheck className="h-4 w-4" />
                        ) : (
                          stepTypeIcons[step.step_type] || <Circle className="h-4 w-4" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium">
                            Step {step.step_number}: {step.title}
                          </h4>
                          {step.is_required && (
                            <Badge variant="secondary">Required</Badge>
                          )}
                        </div>
                        {step.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {step.description}
                          </p>
                        )}
                        {step.completed_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Completed {formatDistanceToNow(new Date(step.completed_at), { addSuffix: true })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Activity Timeline</CardTitle>
              <CardDescription>
                Complete history of onboarding activities
              </CardDescription>
            </CardHeader>
            <CardContent>
              {timeline.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">
                  No activity recorded yet
                </p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
                  <div className="space-y-6">
                    {timeline.map((activity, _index) => (
                      <div key={activity.id} className="relative pl-10">
                        <div className="absolute left-2.5 -translate-x-1/2 w-3 h-3 rounded-full bg-primary border-2 border-background" />
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{activity.action?.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-muted-foreground">{activity.description}</p>
                            {activity.performed_by_name && (
                              <p className="text-xs text-muted-foreground mt-1">
                                By {activity.performed_by_name}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {activity.performed_at
                              ? formatDistanceToNow(new Date(activity.performed_at), { addSuffix: true })
                              : ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Supplier Onboarding</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve {workflow.supplier_name}? This will complete their onboarding process and activate them as an approved supplier.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ThumbsUp className="h-4 w-4 mr-2" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Decline Supplier Onboarding</DialogTitle>
            <DialogDescription>
              Please provide a reason for declining {workflow.supplier_name}'s onboarding. This will be communicated to the supplier.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter reason for declining..."
            value={declineReason}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDeclineReason(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDecline} disabled={actionLoading || !declineReason.trim()}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ThumbsDown className="h-4 w-4 mr-2" />}
              Decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Info Dialog */}
      <Dialog open={infoDialogOpen} onOpenChange={setInfoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Additional Information</DialogTitle>
            <DialogDescription>
              Describe what additional information you need from {workflow.supplier_name}. They will be notified and asked to provide the requested details.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="Enter your request..."
            value={infoRequest}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInfoRequest(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfoDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRequestInfo} disabled={actionLoading || !infoRequest.trim()}>
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MessageSquare className="h-4 w-4 mr-2" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
